import React, { type FC, type PropsWithChildren, useEffect } from 'react'

import {
    ConnectwareError,
    selectAuthenticatedObsolescence,
    selectAuthentication,
    selectDoesRequiresMfa,
    selectIsAuthenticated,
    selectIsLicenseValid,
    SessionFromTokenNotFoundError,
} from '../../../domain'

import Login from '../Login'
import { EnforceMfaForm, PermissionsReducedModal } from '../MFA'
import { useAppState, useAppUsecase } from '../State'
import { LicenseModal } from '../SystemStatus'
import { SessionToken, TokenExpiringModal } from '../Token'

/**
 * These are the barriers of entry to using the application
 *
 * - User either had trouble connecting or has not attempted to do so
 * - Passed token has not yielded a successful authentication
 * - License is invalid or there was an error fetching it
 * - MFA enforced, but not enabled
 *
 * This components handles these barriers
 */
export const Credentials: FC<PropsWithChildren> = ({ children }) => {
    const authenticated = useAppState(selectIsAuthenticated)
    const requiresMfa = useAppState(selectDoesRequiresMfa)
    const sessionObsolescenceStatus = useAppState(selectAuthenticatedObsolescence)
    const hasSessionError = useAppState((s) => SessionFromTokenNotFoundError.is(selectAuthentication(s)))
    const isLicenseValid = useAppState(selectIsLicenseValid)

    const usecase = useAppUsecase('loadLicenseUsecase')
    useEffect(() => usecase.loadLicense(), [usecase, authenticated])

    if (hasSessionError) return <SessionToken />
    if (requiresMfa) return <EnforceMfaForm />
    if (!authenticated) return <Login />
    if (!isLicenseValid) return <LicenseModal />

    return (
        <>
            {sessionObsolescenceStatus !== null && !ConnectwareError.is(sessionObsolescenceStatus) && (
                <PermissionsReducedModal sessionObsolescenceStatus={sessionObsolescenceStatus} />
            )}
            <TokenExpiringModal />
            {children}
        </>
    )
}
